import React, { createContext, useState, useContext, ReactNode } from "react";
import { AxiosResponse } from "axios";
import { calculateFRAPI, getDefaultPaymentTermsAPI } from "services/api/haulpay";
import { IFRCalculationResult, IPaymentSpeed } from "models/HaulPay";
import { showErrorMessage } from "../helpers/toastMessage";
import { getPaymentTermsAPI } from "../services/api/joinHaulpay";

interface BrokerContextType {
  defaultPaymentSettings: IPaymentSpeed[];
  getDefaultPaymentSettings: (factoringId: string) => Promise<void>;
  getBrokerDefaultPaymentSettings: (factoringId: string) => Promise<void>;
  calculateFR: (data: CalculateFRParams) => Promise<IFRCalculationResult>;
  frCalculation: IFRCalculationResult | undefined;
}

interface CalculateFRParams {
  debtor: string;
  factored: boolean;
  invoice_amount: number;
  apply_reserve_holdback?: boolean;
  bill_out_only?: boolean;
  contract_type?: string;
  create_rates_revision?: boolean;
  factoring_fee_split_rate?: number;
  payment_profile?: string;
  payment_profile_amount?: number;
  payment_profile_invoice_amount?: number;
  payment_speed?: number;
  self_finance_requested?: boolean;
}

const BrokerContext = createContext<BrokerContextType | undefined>(undefined);

export const BrokerProvider = ({ children }: { children: ReactNode }) => {
  const [defaultPaymentSettings, setDefaultPaymentSettings] = useState<IPaymentSpeed[]>([]);
  const [frCalculation, setFrCalculation] = useState<IFRCalculationResult>();

  const getDefaultPaymentSettings = async (factoringId: string) => {
    try {
      const paymentSettingsResponse: AxiosResponse<IPaymentSpeed[]> =
        await getDefaultPaymentTermsAPI(factoringId);
      setDefaultPaymentSettings(
        paymentSettingsResponse.data.filter((item) => item.payee === "carrier")
      );
    } catch {
      showErrorMessage("Failed to fetch payment terms");
    }
  };

  const getBrokerDefaultPaymentSettings = async (factoringId: string) => {
    try {
      const paymentSettingsResponse: AxiosResponse<IPaymentSpeed[]> =
        await getPaymentTermsAPI(factoringId);
      setDefaultPaymentSettings(
        paymentSettingsResponse.data.filter((item) => item.payee === "carrier")
      );
    } catch {
      showErrorMessage("Failed to fetch payment terms");
    }
  };

  const calculateFR = async (data: CalculateFRParams) => {
    try {
      const response = await calculateFRAPI(data);
      setFrCalculation(response.data);
      return response.data;
    } catch {
      showErrorMessage("Failed to fetch calculation");
    }
  };

  return (
    <BrokerContext.Provider
      value={{
        defaultPaymentSettings,
        getDefaultPaymentSettings,
        calculateFR,
        frCalculation,
        getBrokerDefaultPaymentSettings
      }}>
      {children}
    </BrokerContext.Provider>
  );
};

export const useBrokerContext = (): BrokerContextType => {
  const context = useContext(BrokerContext);
  if (!context) {
    throw new Error("useBrokerContext must be used within a BrokerProvider");
  }
  return context;
};
